<template>
	<v-app>
		<v-main class="ma-6 pa-0">
			<v-container class="pa-0"
				style="max-width: 445px">
				<!-- TITLE-->
				<h1 class="heading-1">
					{{ t('text.heading.' + actualScreen) }}
				</h1>
				<!-- SUB-TITLE -->
				<p class="body ma-0 mt-2">
					{{ t('text.body.' + actualScreen, { email: email }) }}
				</p>
				<!-- REG. PROGRESS -->
				<v-row
					v-if="actualScreen === 'formScreen' || actualScreen === 'addressFormScreen'"
					class="ma-0 mt-8"
					justify="space-between"
				>
					<h1 class="progressStep"
						v-for="n in 3"
						:key="n"
						aria-hidden="true">
						{{ t('text.progressStep', n - 1) }}
					</h1>
					<v-col cols="12"
						class="px-0">
						<scale-progress-bar
							aria-label="Progress bar"
							:percentage="actualScreen === 'addressFormScreen' ? 90 : 50"
						></scale-progress-bar>
					</v-col>
				</v-row>

				<!-- NOTIFICATION -->
				<VNotification
					:show="notification.show"
					:title="notification.title"
					:text="notification.text"
					:variant="notification.variant"
					@close-click="closeNotification"
				/>

				<!-- FORM SCREEN (registration form)-->
				<div v-if="actualScreen === 'formScreen'">
					<p class="ma-0 mt-4 mb-4"
						id="body-info">
						{{ t('text.bodyInfo.' + actualScreen) }}
					</p>
					<ThePersonalDataForm
						@form-submit="checkExistingData"
						@show-notification="showNotification"
						:loading="updateDataInProgress"
					/>
				</div>

				<!-- DECISION SCREEN (save old or new data ?) -->
				<v-row v-if="actualScreen === 'decisionScreen'"
					class="ma-0">
					<p class="ma-0 mt-8 mb-4"
						style="font-weight: var(--scl-font-variant-heading-3-weight)">
						{{ t('text.bodyInfo.' + actualScreen) }}
					</p>
					<scale-radio-button
						class="mb-4"
						name="idSelectRadio"
						value="old"
						:label="t('oldOrNewRadio.oldOptionText')"
						:checked="choosedData === 'old'"
						@scaleChange="choosedData = $event.target.value"
					/>
					<scale-radio-button
						name="idSelectRadio"
						value="new"
						:label="t('oldOrNewRadio.newOptionText')"
						:checked="choosedData === 'new'"
						@scaleChange="choosedData = $event.target.value"
					/>
				</v-row>

				<!-- REJECTED SCREEN -->
				<v-row v-if="actualScreen === 'rejectedScreen'"
					class="ma-0">
					<p class="ma-0 mt-8 mb-4">
						{{ t('text.bodyInfo.' + actualScreen) }}
					</p>
				</v-row>

				<!-- ADDRESS SCREEN -->
				<v-row v-if="actualScreen === 'addressFormScreen'"
					class="ma-0">
					<p class="ma-0 mt-4 mb-4">
						{{ t('text.bodyInfo.' + actualScreen) }}
					</p>
					<TheAddressForm
						@form-submit="updateAddress"
						@show-notification="showNotification"
						:loading="updateAddressInProgress"
					/>
				</v-row>

				<!-- Primary button -->
				<v-row
					v-if="actualScreen !== 'formScreen' && actualScreen !== 'addressFormScreen'"
					class="ma-0 mt-8 d-flex"
					justify="end"
				>
					<scale-button type="button"
						@click="primaryBtnClicked">
						{{ t('buttons.primaryBtn.' + actualScreen) }}
					</scale-button>
				</v-row>
			</v-container>

			<!-- Confirm dialog -->
			<scale-modal :heading="t('confirmDialog.heading')"
				size="small"
				id="modal">
				<p class="mb-13">{{ t('confirmDialog.description') }}</p>
				<v-row class="ma-0 mb-6"
					justify="end">
					<scale-button class="mr-2"
						variant="secondary"
						@click="setDialogVisibility(false)">
						{{ t('confirmDialog.cancelBtn') }}
					</scale-button>
					<scale-button @click="handleDuplicateData(true)">
						{{ t('confirmDialog.confirmBtn') }}
					</scale-button>
				</v-row>
			</scale-modal>
		</v-main>
	</v-app>
</template>

<script setup>
  import { ref, inject } from 'vue';


  // Components
  import ThePersonalDataForm from './components/ThePersonalDataForm.vue';
  import TheAddressForm from './components/TheAddressForm.vue';
  import VNotification from './components/VNotification.vue';


  // Services
  import RegistrationAPI from './services/RegistrationAPI';
  import SelfServiceAPI from './services/SelfServiceAPI';


  // Plugins
  import { useI18n } from 'vue-i18n';


  // Reusable data
  const { t } = useI18n();
  const keycloak = inject('keycloak');

  const portalLink = window.__CONFIG__.POST_LOGOUT_URL;

  const actualScreen = ref('formScreen');
  const choosedData = ref('old');
  const email = ref('');
  const pernr = ref('');
  const reqStatus = ref('');
  const reqTimeout = ref(60);
  const notification = ref({
    show: false,
    title: '',
    text: []
  });
  const updateDataInProgress = ref(false);
  const updateAddressInProgress = ref(false);

  // Notifications
  const showNotification = (variant, title, text) => {
    notification.value.show = false;
    if (text !== undefined && !Array.isArray(text)) {
      text = [text];
    }
    notification.value = {
      show: true,
      title: title,
      variant: variant,
      text: text
    };
  };

  const closeNotification = () => {
    notification.value = {
      show: false,
      title: '',
      text: []
    };
  };


  // Handle data
  const checkExistingData = (formData) => {
    updateDataInProgress.value = true;
    RegistrationAPI.checkExisting(keycloak.token, formData)
      .then((res) => {
        if (res.status === 200) {
          actualScreen.value = 'addressFormScreen';
          updateDataInProgress.value = false;
        }
      })
      .catch((err) => {
        updateDataInProgress.value = false;
        if (err.response.status === 409) {
          actualScreen.value = "decisionScreen";
          email.value = err.response.data.mail;
          pernr.value = err.response.data.pernr;
        } else if (
          err.response.status === 400 ||
          err.response.status === 500
        ) {
          actualScreen.value = "rejectedScreen";
        } else {
          showNotification(
            "error",
            "Error checking existing data.",
            err.response.data.status + " " + err.response.data.error
          );
          console.error("Error checking existing data -> ", err);
        }
      });
  };

  const handleDuplicateData = (createNew) => {
    setDialogVisibility(false);
    RegistrationAPI.handleDuplicate(keycloak.token, pernr.value, createNew)
      .then(() => {
        if (!createNew) {
          logout();
        } else {
          actualScreen.value = 'addressFormScreen';
        }
      })
      .catch((err) => {
        showNotification(
          "danger",
          "Error handling duplicates.",
          err.response.data.status + " " + err.response.data.error
        );
        console.error("Error handling duplicates -> ", err);
      });
  };

  const updateAddress = (newAddress) => {
    updateAddressInProgress.value = true;
    RegistrationAPI.updateAddress(keycloak.token, newAddress)
      .then((res) => {
        getUpdateRequestStatus(res.data.sessionId);
        updateAddressInProgress.value = false;
      })
      .catch((err) => {
        updateAddressInProgress.value = false;
        showNotification(
          "danger",
          "Error changing address.",
          err.response.data.status + " " + err.response.data.error
        );
        console.error("Error changing address -> ", err);
      });
  };

  // Arrow function for getting status of the update
  const getUpdateRequestStatus = (sessionId) => {
    SelfServiceAPI.getStatusOfUpdate(keycloak.token, sessionId)
      .then((res) => {
        reqStatus.value = res.data.status;
        switch (reqStatus.value) {
        case "PENDING": {
          setTimeout(() => {
            if (reqTimeout.value > 0) {
              reqTimeout.value--;
              getUpdateRequestStatus(sessionId);
            } else {
              reqTimeout.value = 60;
              showNotification("danger", "Timeout", "");
            }
          }, 500);
          break;
        }
        case "SUCCESS": {
          RegistrationAPI.confirmAddress(keycloak.token)
            .then(() => {
              actualScreen.value = "successScreen";
              updateAddressInProgress.value = false;
            })
            .catch((err) => {
              showNotification(
                "danger",
                "Error confirming address.",
                err.response.data.status + " " + err.response.data.error
              );
              console.error("Error confirming address -> ", err);
            });
          break;
        }
        case "FAILED": {
          updateAddressInProgress.value = false;
          showNotification(
            "danger",
            "Request failed (API problem)",
            ""
          );
          break;
        }
        case "ERROR": {
          updateAddressInProgress.value = false;
          showNotification(
            "danger",
            "Error updating data.",
            res.data.errorMsg
          );
          break;
        }
        default: {
          break;
        }
        }
      })
      .catch((err) => {
        updateAddressInProgress.value = false;
        console.error("Error getting status -> ", err);
        if (err.response.error !== undefined) {
          showNotification("danger", err.response.error);
        } else {
          showNotification(
            "danger",
            err.message +
              " " +
              err.response.status +
              " " +
              err.response.statusText
          );
        }
      });
  };


  // Other
  const primaryBtnClicked = () => {
    if (actualScreen.value === 'decisionScreen') {
      if (choosedData.value === 'old') {
        handleDuplicateData(false);
      } else if (choosedData.value === 'new') {
        setDialogVisibility(true);
      }
    } else if (['successScreen', 'rejectedScreen'].includes(actualScreen.value)) {
      logout();
    }
  };

  const setDialogVisibility = (visibility) => {
    const modal = document.getElementById('modal');
    modal.opened = visibility;
  };

  const logout = () => {
    window.location.href = portalLink;
  };
</script>

<style lang="scss">
#app {
  font-family: var(--scl-font-family-sans);
}
.heading-1 {
  font: var(--scl-font-variant-heading-3-weight)
    (
      var(--scl-font-variant-heading-3-size) + "/" +
        var(--scl-font-variant-heading-3-line-height)
    )
    var(--scl-font-variant-heading-3-family);
}
.body {
  font: var(--scl-font-weight-thin)
    (
      var(--scl-font-variant-heading-3-size) + "/" +
        var(--scl-font-variant-heading-1-line-height)
    )
    var(--scl-font-variant-heading-3-family);
}
.progressStep {
  font: var(--scl-font-variant-heading-3-weight)
    (
      var(--scl-font-variant-heading-4-size) + "/" +
        var(--scl-font-variant-heading-4-line-height)
    )
    var(--scl-font-variant-heading-3-family);
}
scale-text-field {
  line-height: initial;
}
</style>
